import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import RadioButton from "./RadioButton";
import SearchBox from "./SearchBox";
import { getPayorAddresses, getAddresses, getStates } from "../services/soar";
import {
  createEntity,
  getFieldName,
  getFieldText,
  getFieldDate,
  updateEntity,
} from "./../../scripts/helperScripts";
import clientPayorOptionSets from "../../options/clientpayor.json";

function ClientPayorModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  initialData,
  clientId,
  clientProgram,
  updateClientPayors,
  updateAddresses,
  addresses,
  careTeam,
  addressBeingCreated,
}) {
  const [data, setData] = useState({
    ccx_selfpolicyholder: "1",
    ccx_clientpayorid: "",
  });
  const [addressData, setAddressData] = useState({});
  const [allPayors, setAllPayors] = useState([]);
  const [loadingPayors, setLoadingPayors] = useState(false);
  const [payorSearchQuery, setPayorSearchQuery] = useState("");
  const [allGroupPlans, setAllGroupPlans] = useState([]);
  const [allGroupPlanAddresses, setAllGroupPlanAddresses] = useState([]);
  const [familyAddresses, setFamilyAddresses] = useState([]);
  const [addressStates, setAddressStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [controllerSignal, setControllerSignal] = useState();
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const clientProgramAdmitDate = useMemo(() => {
    if (!clientProgram) {
      return null;
    }

    return moment(getFieldDate(clientProgram.ccx_admitdate));
  }, [clientProgram]);

  const joiSchema = {
    ccx_clientpayorid: Joi.string().allow(""),
    ccx_effdate: Joi.alternatives([
      Joi.date()
        .required()
        .max(
          clientProgramAdmitDate.isAfter(moment())
            ? clientProgramAdmitDate.toDate()
            : "now"
        )
        .min(new Date("1/1/1900"))
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.base":
                error.message = `Effective Date is required`;
                break;
              case "date.max":
                error.message = `Effective Date cannot be future dated beyond ${moment(
                  error.context.limit
                ).format("L")}`;
                break;
              case "date.min":
                error.message = `Effective Date cannot be before 1/1/1900`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
    ]),
    ccx_expdate: Joi.alternatives([
      Joi.date()
        .max(
          moment(data.ccx_effdate).isAfter(moment())
            ? new Date(data.ccx_effdate)
            : "now"
        )
        .min(new Date(data.ccx_effdate))
        .allow(null)
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `Expiration Date cannot be before Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              case "date.max":
                error.message = `Expiration Date cannot be future dated beyond Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      Joi.string().valid(""),
    ]),
    ccx_payor: Joi.string().required().label("Payor"),
    ccx_payorgroupplan: Joi.string().required().label("Payor Group Plan"),
    ccx_groupplanpayoraddress: Joi.string()
      .required()
      .label("Group Plan Address"),
    ccx_payorrank: Joi.string().required().label("Rank"),
    ccx_id1: Joi.string().max(100).allow("").label("ID 1"),
    ccx_id2: Joi.string().max(100).allow("").label("ID 2"),
    ccx_selfpolicyholder: Joi.string().required().label("Policy Holder"),
    ccx_familymember: Joi.string()
      .allow("")
      .label("Family Member")
      .when("ccx_selfpolicyholder", {
        is: "0",
        then: Joi.string().required(),
      }),
    ccx_contactaddress: Joi.string().label("Contact Address").required(),
    ccx_notes: Joi.string().max(2000).allow(""),
  };

  const addressSchema = {
    ccx_line1: Joi.string().label("Street Line 1").required(),
    ccx_city: Joi.string().label("City").required(),
    ccx_state_lu: Joi.string().label("State").required(),
    ccx_postalcode: Joi.string().label("Postal Code").required(),
  };

  const payorOptions = useMemo(() => {
    if (!payorSearchQuery) {
      return allPayors;
    }

    return allPayors.filter((payor) =>
      payor.name.toLowerCase().includes(payorSearchQuery.toLowerCase())
    );
  }, [allPayors, payorSearchQuery]);

  const groupPlanOptions = useMemo(() => {
    return allGroupPlans.filter(
      (groupPlan) => groupPlan.payorId === data.ccx_payor
    );
  }, [allGroupPlans, data.ccx_payor]);

  const groupPlanAddressOptions = useMemo(() => {
    return allGroupPlanAddresses.filter(
      (address) => address.groupPlanId === data.ccx_payorgroupplan
    );
  }, [allGroupPlanAddresses, data.ccx_payorgroupplan]);

  const collateralOptions = useMemo(() => {
    if (careTeam === undefined || careTeam.length === 0) {
      return [];
    }

    return careTeam
      .filter(
        (member) =>
          getFieldText(member.ccx_membertype) === "803080001" &&
          member.ccx_expdate == null &&
          member.ccx_contact != null
      )
      .map((contact) => {
        return {
          _id: getFieldText(contact.ccx_contact),
          name: getFieldName(contact.ccx_contact),
        };
      });
  }, [careTeam]);

  const homeAddress = useMemo(() => {
    if (addresses?.length === 0) {
      return;
    }

    return addresses.filter(
      (address) =>
        !address.ccx_expirationdate ||
        moment(getFieldDate(address.ccx_expirationdate)).isAfter(
          moment(data.ccx_effdate)
        )
    )[0];
  }, [addresses, data.ccx_effdate]);

  const familyAddressOptions = useMemo(() => {
    return familyAddresses.map((address) => {
      const option = {
        _id: address.ccx_contactaddressid,
        name: `${address.ccx_line1} ${address.ccx_city}, ${address.ccx_state} ${address.ccx_postalcode}`,
      };

      return option;
    });
  }, [familyAddresses]);

  const newAddressNeeded = useMemo(() => {
    if (data.ccx_selfpolicyholder === "1") {
      if (!homeAddress) {
        return true;
      }

      return false;
    }

    if (!data.ccx_familymember || familyAddresses.length > 0) {
      return false;
    }

    return true;
  }, [
    data.ccx_clientpayorid,
    data.ccx_selfpolicyholder,
    data.ccx_familymember,
    homeAddress,
    familyAddresses,
  ]);

  useEffect(() => {
    if (!safyInfo?.user?.safyInfo?.BusinessUnitIdName) {
      navigate("/");
    }
  });

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (!initialData) {
      if (homeAddress) {
        setData((d) => {
          const obj = { ...d };
          obj.ccx_contactaddress = homeAddress.ccx_contactaddressid;
          return obj;
        });
      }
    } else {
      const obj = { ...initialData };

      if (obj.ccx_payor) {
        const payorOption = {
          _id: obj.ccx_payor,
          name: obj.payorName,
        };
        setAllPayors([payorOption]);

        delete obj.payorName;
      }

      if (obj.ccx_payorgroupplan) {
        const groupPlanOption = {
          _id: obj.ccx_payorgroupplan,
          name: obj.payorGroupPlanName,
          payorId: obj.ccx_payor,
        };
        setAllGroupPlans([groupPlanOption]);

        delete obj.payorGroupPlanName;
      }

      if (obj.ccx_groupplanpayoraddress) {
        const groupPlanAddressOption = {
          _id: obj.ccx_groupplanpayoraddress,
          name: obj.groupPlanAddressName,
          groupPlanId: obj.ccx_payorgroupplan,
        };
        setAllGroupPlanAddresses([groupPlanAddressOption]);

        delete obj.groupPlanAddressName;
      }

      if (obj.ccx_selfpolicyholder === "0" && obj.ccx_familymember) {
        const signal = controller.signal;
        const loadAddresses = async () => {
          const response = await getAddresses(obj.ccx_familymember, signal);

          if (signal?.aborted || !response) {
            return;
          }

          if (response.length === 1) {
            obj.ccx_contactaddress = response[0].ccx_contactaddressid;
          }

          setData(obj);
          setFamilyAddresses(response);
        };

        loadAddresses();
      } else {
        setData(obj);
      }
    }

    return () => {
      controller.abort();
    };
  }, [initialData, homeAddress]);

  useEffect(() => {
    if (addressBeingCreated || !newAddressNeeded) {
      return;
    }

    const loadStates = async () => {
      const response = await getStates(controllerSignal);
      if (controllerSignal?.aborted || !response) {
        return;
      }

      const states = response.map((s) => ({
        _id: s.ccx_stateid,
        name: s.ccx_name,
        abbreviation: s.ccx_abbreviation,
      }));

      setAddressStates(states);
    };

    loadStates();
  }, [addressBeingCreated, newAddressNeeded, controllerSignal]);

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, joiSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);
    setErrors(errs);
  };

  const handleAddressDataChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, addressSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const obj = { ...addressData };
    obj[input.name] = input.value;
    setAddressData(obj);
    setErrors(errs);
  };

  const handleEffectiveDateChange = async (e) => {
    handleChange(e);
    const input = e.currentTarget;

    if (data.ccx_clientpayorid || input.value === "") {
      return;
    }

    const effectiveDate = moment(input.value);

    if (effectiveDate.year() < 2000) {
      return;
    }

    loadPayors(input.value);
  };

  const loadPayors = async (effectiveDate) => {
    console.log("loadPayors");
    setLoadingPayors(true);
    const response = await getPayorAddresses(
      effectiveDate,
      getFieldName(clientProgram.ccx_company),
      controllerSignal
    );

    if (controllerSignal?.aborted || !response) {
      return;
    }

    let payors = [];
    const payorIds = [];
    let groupPlans = [];
    const groupPlanIds = [];
    const groupPlanAddresses = [];
    response.forEach((address) => {
      const payorId = getFieldText(address["ai.ccx_payor"]);
      if (!payorIds.includes(payorId)) {
        payorIds.push(payorId);
        payors.push({
          _id: payorId,
          name: getFieldName(address["ai.ccx_payor"]),
        });
      }

      const groupPlanId = getFieldText(address.ccx_payorgroupplan);
      if (!groupPlanIds.includes(groupPlanId)) {
        groupPlanIds.push(groupPlanId);
        groupPlans.push({
          _id: groupPlanId,
          name: getFieldName(address.ccx_payorgroupplan),
          groupId: address["ai.ccx_groupid"],
          payorId: payorId,
        });
      }

      groupPlanAddresses.push({
        _id: address.ccx_groupplanpayoraddressid,
        name: address.ccx_name,
        groupPlanId: groupPlanId,
      });
    });

    payors = payors.sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
    setAllPayors(payors);

    groupPlans = groupPlans.sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
    setAllGroupPlans(groupPlans);
    setAllGroupPlanAddresses(groupPlanAddresses);
    setLoadingPayors(false);
  };

  const handlePayorChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, joiSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    const obj = { ...data };
    obj.ccx_payor = input.value;
    delete obj.ccx_payorgroupplan;
    delete obj.ccx_groupplanpayoraddress;
    const groupPlans = allGroupPlans.filter(
      (groupPlan) => groupPlan.payorId === input.value
    );

    if (groupPlans.length === 0 || groupPlans.length > 1) {
      setData(obj);
      setErrors(errs);
      return;
    }

    delete errs.ccx_payorgroupplan;
    obj.ccx_payorgroupplan = groupPlans[0]._id;
    const groupPlanAddresses = allGroupPlanAddresses.filter(
      (address) => address.groupPlanId === groupPlans[0]._id
    );

    if (groupPlanAddresses.length === 0 || groupPlanAddresses.length > 1) {
      setData(obj);
      setErrors(errs);
      return;
    }

    delete errs.ccx_groupplanpayoraddress;
    obj.ccx_groupplanpayoraddress = groupPlanAddresses[0]._id;
    setData(obj);
    setErrors(errs);
  };

  const handleGroupPlanChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input, joiSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    const obj = { ...data };
    obj.ccx_payorgroupplan = input.value;
    delete obj.ccx_groupplanpayoraddress;
    const groupPlanAddresses = allGroupPlanAddresses.filter(
      (address) => address.groupPlanId === input.value
    );

    if (groupPlanAddresses.length === 0 || groupPlanAddresses.length > 1) {
      setData(obj);
      setErrors(errs);
      return;
    }

    delete errs.ccx_groupplanpayoraddress;
    obj.ccx_groupplanpayoraddress = groupPlanAddresses[0]._id;
    setData(obj);
    setErrors(errs);
  };

  const handleFamilyMemberChange = async ({ currentTarget: input }) => {
    setFamilyAddresses([]);
    setData((d) => {
      const obj = { ...d };
      obj.ccx_familymember = input.value;
      return obj;
    });

    if (!input.value) {
      return;
    }

    const response = await getAddresses(input.value, controllerSignal);

    if (controllerSignal?.aborted || !response) {
      return;
    }

    setFamilyAddresses(response);

    if (response.length === 1) {
      setData((d) => {
        const obj = { ...d };
        obj.ccx_contactaddress = response[0].ccx_contactaddressid;
        return obj;
      });
    }
  };

  const validateProperty = (input, schema) => {
    const obj = { [input.name]: input.value };
    const propertySchema = { [input.name]: schema[input.name] };
    const { error } = Joi.validate(obj, propertySchema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);

    let validationErrors = {};
    if (error?.details) {
      for (let item of error.details) {
        validationErrors[item.path[0]] = item.message;
      }
    }

    let addressError = {};
    if (newAddressNeeded) {
      delete validationErrors.ccx_contactaddress;
      addressError = Joi.validate(addressData, addressSchema, options).error;

      if (addressError?.details) {
        for (let item of addressError.details) {
          validationErrors[item.path[0]] = item.message;
        }
      }
    }

    if (Object.keys(validationErrors).length === 0) {
      return null;
    }

    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = async () => {
    const groupPlan = groupPlanOptions.filter(
      (option) => option._id === data.ccx_payorgroupplan
    )[0];

    const obj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      sor_clientcompany: {
        type: "entity",
        value: {
          entityName: "ccx_clientagency",
          entityId: getFieldText(clientProgram.ccx_company),
        },
      },
      ccx_effdate: {
        type: "datetime",
        value: new Date(data.ccx_effdate),
      },
      ccx_payor: {
        type: "entity",
        value: {
          entityName: "ccx_payor",
          entityId: data.ccx_payor,
        },
      },
      ccx_payorgroupplan: {
        type: "entity",
        value: {
          entityName: "ccx_payorgroupplan",
          entityId: data.ccx_payorgroupplan,
        },
      },
      ccx_groupplanpayoraddress: {
        type: "entity",
        value: {
          entityName: "ccx_groupplanpayoraddress",
          entityId: data.ccx_groupplanpayoraddress,
        },
      },
      ccx_payorrank: {
        type: "optionset",
        value: parseInt(data.ccx_payorrank),
      },
      ccx_id1: {
        type: "string",
        value: data.ccx_id1 || "",
      },
      ccx_id2: {
        type: "string",
        value: data.ccx_id2 || "",
      },
      ccx_notes: {
        type: "string",
        value: data.ccx_notes || "",
      },
      ccx_selfpolicyholder: {
        type: "bool",
        value: data.ccx_selfpolicyholder === "1",
      },
      ccx_groupname: {
        type: "string",
        value: groupPlan.name,
      },
      ccx_groupid: {
        type: "string",
        value: groupPlan.groupId,
      },
      ccx_expdate: { type: "datetime", value: null },
    };

    if (data.ccx_familymember) {
      obj.ccx_familymember = {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: data.ccx_familymember,
        },
      };
    }

    if (data.ccx_expdate) {
      obj.ccx_expdate.value = new Date(data.ccx_expdate);
    }

    if (newAddressNeeded) {
      const state = addressStates.filter(
        (as) => as._id === addressData.ccx_state_lu
      )[0];
      const address = {
        ccx_parentid: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId:
              data.ccx_selfpolicyholder === "1"
                ? clientId
                : data.ccx_familymember,
          },
        },
        ccx_addresstype: {
          type: "optionset",
          value: 100000000,
        },
        ccx_effectivedate: {
          type: "datetime",
          value: new Date(data.ccx_effdate),
        },
        ccx_line1: {
          type: "string",
          value: addressData.ccx_line1,
        },
        ccx_city: {
          type: "string",
          value: addressData.ccx_city,
        },
        ccx_state_lu: {
          type: "entity",
          value: {
            entityName: "ccx_state",
            entityId: addressData.ccx_state_lu,
          },
        },
        ccx_state: {
          type: "string",
          value: state.abbreviation,
        },
        ccx_postalcode: {
          type: "string",
          value: addressData.ccx_postalcode,
        },
      };
      const toastId = toast.loading("Address is being created...", {
        type: toast.TYPE.INFO,
      });
      console.log(
        "addressCreate",
        safyInfo,
        JSON.stringify(address),
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      );

      try {
        const createAddressResponse = await createEntity(
          "ccx_contactaddress",
          JSON.stringify(address),
          safyInfo,
          getFieldName(clientProgram.ccx_company).split(" of ")[1]
        );
        toast.update(toastId, {
          render: "Address has been created. Payor is being created...",
        });
        updateAddresses?.(true);

        obj.ccx_contactaddress = {
          type: "entity",
          value: {
            entityName: "ccx_contactaddress",
            entityId:
              createAddressResponse.resultset.result.ccx_contactaddressid,
          },
        };

        setData((d) => {
          const obj = { ...d };
          obj.ccx_contactaddress =
            createAddressResponse.resultset.result.ccx_contactaddressid;
          return obj;
        });

        try {
          await createEntity(
            "ccx_clientpayor",
            JSON.stringify(obj),
            safyInfo,
            getFieldName(clientProgram.ccx_company).split(" of ")[1]
          );
          updateClientPayors?.(true);
          toast.update(toastId, {
            render: "Payor has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        } catch {
          toast.update(toastId, {
            render: "An error occurred while creating the Payor.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        }
      } catch {
        toast.update(toastId, {
          render: "An error occurred while creating the Address.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          closeOnClick: true,
          autoClose: 5000,
        });
      }
    } else {
      obj.ccx_contactaddress = {
        type: "entity",
        value: {
          entityName: "ccx_contactaddress",
          entityId: data.ccx_contactaddress,
        },
      };

      if (data.ccx_clientpayorid) {
        const toastId = toast.loading("Payor is being updated...", {
          type: toast.TYPE.INFO,
        });
        try {
          await updateEntity(
            "ccx_clientpayor",
            data.ccx_clientpayorid,
            JSON.stringify(obj),
            safyInfo
          );
          updateClientPayors?.(true);
          toast.update(toastId, {
            render: "Payor has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        } catch {
          toast.update(toastId, {
            render: "An error occurred while updating the Payor.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        }
      } else {
        const toastId = toast.loading("Payor is being created...", {
          type: toast.TYPE.INFO,
        });
        try {
          await createEntity(
            "ccx_clientpayor",
            JSON.stringify(obj),
            safyInfo,
            getFieldName(clientProgram.ccx_company).split(" of ")[1]
          );
          updateClientPayors?.(true);
          toast.update(toastId, {
            render: "Payor has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        } catch {
          toast.update(toastId, {
            render: "An error occurred while creating the Payor.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        }
      }
    }
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    setData((d) => {
      const obj = {};
      obj.ccx_effdate = d.ccx_effdate;
      obj.ccx_selfpolicyholder = d.ccx_selfpolicyholder;
      obj.ccx_contactaddress = d.ccx_contactaddress;
      obj.ccx_payorrank = "";
      obj.ccx_id1 = "";
      obj.ccx_id2 = "";
      obj.ccx_notes = "";
      obj.ccx_familymember = "";
      return obj;
    });
    setAddressData({});
    setPayorSearchQuery("");
    setErrors({});
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false} backdrop="static">
          <Modal.Header>
            <Modal.Title>Client Payor</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col">
                  <Input
                    name="ccx_effdate"
                    label="Effective Date"
                    value={data.ccx_effdate}
                    onChange={handleEffectiveDateChange}
                    error={errors.ccx_effdate}
                    disabled={data.ccx_clientpayorid || !clientProgram}
                    type="date"
                  />
                </div>
                <div className="col">
                  {!data.ccx_clientpayorid && (
                    <SearchBox
                      name="PayorSearch"
                      label="Search for Payor"
                      placeholder="Search for Payor..."
                      value={payorSearchQuery}
                      onChange={({ currentTarget: input }) => {
                        setPayorSearchQuery(input.value);
                      }}
                      disabled={!data.ccx_effdate}
                    ></SearchBox>
                  )}
                </div>
              </div>
            </div>
            {!data.ccx_groupplanpayoraddress ? (
              <>
                {loadingPayors ? (
                  <div className="mb-2">Payors loading...</div>
                ) : (
                  <Select
                    name="ccx_payor"
                    label="Payor"
                    value={data.ccx_payor}
                    options={payorOptions}
                    onChange={handlePayorChange}
                    error={errors.ccx_payor}
                    disabled={!data.ccx_effdate || !clientProgram}
                  />
                )}
                <Select
                  name="ccx_payorgroupplan"
                  label="Payor Group Plan"
                  value={data.ccx_payorgroupplan}
                  options={groupPlanOptions}
                  onChange={handleGroupPlanChange}
                  error={errors.ccx_payorgroupplan}
                  disabled={!data.ccx_payor}
                />
                <Select
                  name="ccx_groupplanpayoraddress"
                  label="Group Plan Address"
                  value={data.ccx_groupplanpayoraddress}
                  options={groupPlanAddressOptions}
                  onChange={handleChange}
                  error={errors.ccx_groupplanpayoraddress}
                  disabled={!data.ccx_payorgroupplan}
                />
              </>
            ) : (
              <>
                <div>
                  {data.ccx_clientpayorid === "" && (
                    <i
                      className="fas fa-minus-circle me-2"
                      onClick={() => {
                        setData((d) => {
                          const obj = { ...d };
                          delete obj.ccx_payor;
                          delete obj.ccx_payorgroupplan;
                          delete obj.ccx_groupplanpayoraddress;
                          return obj;
                        });
                      }}
                    ></i>
                  )}
                  {
                    payorOptions.filter(
                      (option) => option._id === data.ccx_payor
                    )[0]?.name
                  }
                </div>
                <div>
                  {
                    groupPlanOptions.filter(
                      (option) => option._id === data.ccx_payorgroupplan
                    )[0]?.name
                  }
                </div>
                <div>
                  {
                    groupPlanAddressOptions.filter(
                      (option) => option._id === data.ccx_groupplanpayoraddress
                    )[0]?.name
                  }
                </div>
              </>
            )}
            <div className="container">
              <div className="row">
                <div className="col">
                  <Select
                    name="ccx_payorrank"
                    label="Rank"
                    value={data.ccx_payorrank}
                    onChange={handleChange}
                    options={clientPayorOptionSets.ccx_payorrank}
                    error={errors.ccx_payorrank}
                  />
                </div>
                <div className="col"></div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="ccx_id1"
                    label="ID 1"
                    value={data.ccx_id1}
                    onChange={handleChange}
                  />
                </div>
                <div className="col">
                  <Input
                    name="ccx_id2"
                    label="ID 2"
                    value={data.ccx_id2}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {data.ccx_clientpayorid && (
                    <Input
                      name="ccx_expdate"
                      label="Expiration Date"
                      value={data.ccx_expdate}
                      onChange={handleChange}
                      type="date"
                      error={errors.ccx_expdate}
                    />
                  )}
                </div>
                <div className="col"></div>
              </div>
            </div>
            <RadioButton
              name="ccx_selfpolicyholder"
              label="Policy Holder:"
              value={data.ccx_selfpolicyholder}
              onChange={handleChange}
              options={clientPayorOptionSets.ccx_selfpolicyholder}
              error={errors.ccx_selfpolicyholder}
              disabled={data.ccx_clientpayorid}
            />
            <Input
              name="ccx_notes"
              label="Notes"
              value={data.ccx_notes}
              onChange={handleChange}
            />
            {data.ccx_selfpolicyholder === "0" ? (
              <>
                <Select
                  name="ccx_familymember"
                  label="Family Member"
                  value={data.ccx_familymember}
                  onChange={handleFamilyMemberChange}
                  options={collateralOptions}
                  error={errors.ccx_familymember}
                  disabled={data.ccx_clientpayorid}
                />
                {!addressBeingCreated && familyAddressOptions.length > 0 ? (
                  <Select
                    name="ccx_contactaddress"
                    label="Contact Address"
                    value={data.ccx_contactaddress}
                    options={familyAddressOptions}
                    onChange={handleChange}
                    error={errors.ccx_contactaddress}
                    disabled={data.ccx_clientpayorid}
                  />
                ) : (
                  <>
                    <Input
                      name="ccx_line1"
                      label="Street Line 1"
                      value={data.ccx_line1}
                      onChange={handleAddressDataChange}
                      error={errors.ccx_line1}
                    />
                    <Input
                      name="ccx_city"
                      label="City"
                      value={data.ccx_city}
                      onChange={handleAddressDataChange}
                      error={errors.ccx_city}
                    />
                    <Select
                      name="ccx_state_lu"
                      label="State"
                      value={data.ccx_state_lu}
                      options={addressStates}
                      onChange={handleAddressDataChange}
                      error={errors.ccx_state_lu}
                      disabled={addressStates.length === 0}
                    />
                    <Input
                      name="ccx_postalcode"
                      label="Postal Code"
                      value={data.ccx_postalcode}
                      onChange={handleAddressDataChange}
                      error={errors.ccx_postalcode}
                    />
                  </>
                )}
              </>
            ) : !addressBeingCreated && homeAddress ? (
              <Select
                name="ccx_contactaddress"
                label="Contact Address"
                value={data.ccx_contactaddress}
                onChange={handleChange}
                options={[
                  {
                    _id: homeAddress.ccx_contactaddressid,
                    name: `${homeAddress.ccx_line1} ${homeAddress.ccx_city}, ${homeAddress.ccx_state} ${homeAddress.ccx_postalcode}`,
                  },
                ]}
                disabled={initialData?.ccx_contactaddress !== undefined}
              />
            ) : (
              <>
                <Input
                  name="ccx_line1"
                  label="Street Line 1"
                  value={addressData.ccx_line1}
                  onChange={handleAddressDataChange}
                  error={errors.ccx_line1}
                />
                <Input
                  name="ccx_city"
                  label="City"
                  value={addressData.ccx_city}
                  onChange={handleAddressDataChange}
                  error={errors.ccx_city}
                />
                <Select
                  name="ccx_state_lu"
                  label="State"
                  value={addressData.ccx_state_lu}
                  options={addressStates}
                  onChange={handleAddressDataChange}
                  error={errors.ccx_state_lu}
                  disabled={addressStates.length === 0}
                />
                <Input
                  name="ccx_postalcode"
                  label="Postal Code"
                  value={addressData.ccx_postalcode}
                  onChange={handleAddressDataChange}
                  error={errors.ccx_postalcode}
                />
              </>
            )}
            {errors.ccx_contactaddress && (
              <div className="alert alert-danger">
                {errors.ccx_contactaddress}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={hideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      hideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default ClientPayorModal;
